import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect } from 'react';
import { TrainingStore } from '../';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

const TrainingViewFinish = observer(({ store, failed }: { store: any; failed: any }) => {
    const bgColor = failed ? '#f8d7da' : '#d4edda'; // red for failed, green for success
    const textColor = failed ? '#721c24' : '#155724';
    const icon = failed ? 'pi-times-circle' : 'pi-check-circle';

    return (
        <div
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: bgColor,
                color: textColor,
                padding: '2rem',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                textAlign: 'center',
                zIndex: 1000,
                width: '80%',
                maxWidth: '500px',
            }}
        >
            <i
                className={`pi ${icon}`}
                style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}
            ></i>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
                {failed ? (
                    <>Schulung nicht bestanden. Versuchen Sie es erneut! </>
                ) : (
                    <> Schulung erfolgreich abgeschlossen!</>
                )}
            </h2>
            <Button
                label="Zurück"
                onClick={() => store.quitTraining()}
                style={{
                    marginTop: '1rem',
                    backgroundColor: '#ff6f61',
                    color: '#fff',
                    padding: '10px 20px',
                    fontSize: '16px',
                    borderRadius: '5px',
                }}
            />
        </div>
    );
});
const TrainingViewQuestion = observer(({ store }: { store: any }) => {
    const useTraining = store?.currentTraining || null;
    const useSession = useTraining.session || null;
    const isFinished = useSession.question.finished;
    const isQuestionCorrect = useSession.question.correct === true;

    return (
        <div style={{ width: '100%' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 'bold' }}>
                {useTraining.session.question.question}
            </h2>
            {useTraining.session.question.multiple && <i>Mehrere Antworten möglich</i>}
            {isFinished && (
                <div style={{ marginBottom: '20px' }}>
                    {useSession.question.correct ? (
                        <Message
                            severity="success"
                            text="Ihre Antwort ist richtig"
                            icon="pi pi-check-circle"
                            style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}
                        />
                    ) : (
                        <Message
                            severity="error"
                            text="Ihre Antwort ist falsch"
                            icon="pi pi-times-circle"
                            style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}
                        />
                    )}
                </div>
            )}
            {useTraining.session.question.answers.map((answer: any, index: number) => {
                const isCorrect =
                    (useSession.question.correct === true ||
                        useSession.question.correct === false) &&
                    (useTraining.session.question.correctAnswers || []).includes(answer.answer_id);
                const isWrong =
                    (useSession.question.correct === true ||
                        useSession.question.correct === false) &&
                    !(useTraining.session.question.correctAnswers || []).includes(answer.answer_id);
                const isSelected = store.currentAnswer.includes(answer.answer_id);

                let useBackgroundColor = '#fff';
                let useTextColor = '#000';

                if (isCorrect) {
                    useBackgroundColor = '#4CAF50';
                    useTextColor = '#fff';
                } else if (isWrong && isSelected) {
                    useBackgroundColor = '#dc3545';
                    useTextColor = '#fff';
                } else if (isSelected) {
                    useBackgroundColor = '#007bff';
                    useTextColor = '#fff';
                }

                return (
                    <div
                        key={`answer-${index}`}
                        onClick={() => store.selectAnswer(answer.answer_id)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '15px',
                            marginBottom: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            backgroundColor: useBackgroundColor,
                            color: useTextColor,
                            cursor: 'pointer',
                        }}
                    >
                        {isCorrect && (
                            <i
                                className="pi pi-check-circle"
                                style={{ float: 'left', marginRight: '10px', color: useTextColor }}
                            ></i>
                        )}
                        {isWrong && (
                            <i
                                className="pi pi-times-circle"
                                style={{ float: 'left', marginRight: '10px', color: useTextColor }}
                            ></i>
                        )}
                        <span>{answer.answer}</span>
                    </div>
                );
            })}
            {isFinished && (
                <Button
                    label={isQuestionCorrect ? 'Nächste Frage' : 'Weiter'}
                    onClick={() => {
                        isQuestionCorrect ? store.nextQuestion() : store.trainingFinishFailed();
                    }}
                    style={{
                        width: '100%',
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        padding: '15px',
                        borderRadius: '5px',
                        marginTop: '20px',
                    }}
                />
            )}
            {!isFinished && (
                <Button
                    label="Antwort senden"
                    onClick={() => store.answerQuestion(store.currentAnswer)}
                    disabled={store.currentAnswer.length === 0}
                    style={{
                        width: '100%',
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        padding: '15px',
                        fontSize: '16px',
                        borderRadius: '5px',
                        marginTop: '20px',
                    }}
                />
            )}
        </div>
    );
});

const TrainingViewPdfPreview = observer(({ store, training }: { store: any; training: any }) => {
    return (
        <div
            style={{ textAlign: 'center', cursor: 'pointer' }}
            onClick={() => store.setPdfFullscreenMode(true)}
        >
            <div
                style={{
                    width: '100%',
                    height: '300px',
                    backgroundColor: '#f5f5f5',
                    border: '2px dashed #ccc',
                    borderRadius: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    transition: 'all 0.3s ease',
                }}
                onMouseOver={e => {
                    e.currentTarget.style.backgroundColor = '#e8f5e9';
                    e.currentTarget.style.borderColor = '#4CAF50';
                }}
                onMouseOut={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#ccc';
                }}
            >
                <i
                    className="pi pi-file-pdf"
                    style={{ fontSize: '4rem', color: '#d32f2f', marginBottom: '1rem' }}
                ></i>
                <h3 style={{ margin: 0, color: '#666', fontSize: '1.2rem' }}>PDF-Schulung</h3>
                <p style={{ margin: '0.5rem 0 0 0', color: '#888', fontSize: '1rem' }}>
                    Klicken Sie hier, um die PDF zu öffnen
                </p>
            </div>
        </div>
    );
});

const TrainingViewPdfFullscreen = observer(({ store, training }: { store: any; training: any }) => {
    // PDF.js Viewer URL - verwende den eingebauten Viewer
    const pdfViewerUrl = `/pdf2js/web/viewer.html?file=${encodeURIComponent(training.pdfUrl)}`;

    return (
        <div
            style={{
                position: 'fixed',
                top: '0',
                left: '0',
                width: '100vw',
                height: '100vh',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 9999,
                WebkitTransform: 'translateZ(0)', // iOS Hardware-Beschleunigung
                transform: 'translateZ(0)',
                WebkitBackfaceVisibility: 'hidden', // iOS Rendering-Fix
                backfaceVisibility: 'hidden',
                isolation: 'isolate', // Neue Stacking-Context für ältere Browser
                WebkitPerspective: 1000, // iOS 3D-Rendering aktivieren
                perspective: 1000,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 10000,
                }}
            >
                <button
                    onClick={() => store.setPdfFullscreenMode(false)}
                    style={{
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        fontSize: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        WebkitTransform: 'translateZ(0)',
                        transform: 'translateZ(0)',
                        WebkitBackfaceVisibility: 'hidden',
                        backfaceVisibility: 'hidden',
                    }}
                >
                    ×
                </button>
            </div>
            <div
                style={{
                    flex: 1,
                    padding: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden', // PDF.js Viewer handhabt sein eigenes Scrolling
                }}
            >
                <iframe
                    src={pdfViewerUrl}
                    title={training.label}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    allowFullScreen
                />
            </div>
        </div>
    );
});

const TrainingNavigation = observer(({ store }: { store: any }) => {
    const useTraining = store?.currentTraining || null;

    return (
        <>
            {useTraining?.session ? (
                <>
                    {useTraining.session.finished ? (
                        <TrainingViewFinish
                            store={store}
                            failed={useTraining.session.failed}
                        />
                    ) : (
                        <TrainingViewQuestion store={store} />
                    )}
                </>
            ) : (
                <>
                    {store?.pdfFullscreenMode && useTraining?.contentType === 'pdf' ? (
                        <TrainingViewPdfFullscreen
                            store={store}
                            training={useTraining}
                        />
                    ) : (
                        <>
                            {useTraining.img && (
                                <img
                                    src={useTraining.img}
                                    alt="Training Bild"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '12px',
                                        marginBottom: '1rem',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                            <h2 style={{ fontSize: '1.5rem', margin: 0, fontWeight: 'bold' }}>
                                {useTraining.label}
                            </h2>
                            <p style={{ fontSize: '1rem', margin: 0, color: '#555' }}>
                                {useTraining.data}
                            </p>
                            {useTraining.contentType === 'video' && useTraining.video && (
                                <iframe
                                    width="100%"
                                    src={useTraining.video}
                                    title={useTraining.label}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{ borderRadius: '10px' }}
                                ></iframe>
                            )}
                            {useTraining.contentType === 'pdf' && useTraining.pdfUrl && (
                                <TrainingViewPdfPreview
                                    store={store}
                                    training={useTraining}
                                />
                            )}
                            {!!useTraining.completed && (
                                <Message
                                    severity="info"
                                    text="Schulung wurde bereits erfolgreich abgeschlossen"
                                    icon="pi pi-check-circle"
                                    style={{
                                        marginTop: '1rem',
                                        fontSize: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#e8f5e9',
                                        color: '#388e3c',
                                        border: '1px solid #c8e6c9',
                                        borderRadius: '5px',
                                        padding: '10px',
                                    }}
                                />
                            )}
                            {!useTraining.completed && (
                                <Button
                                    label="Prüfung starten"
                                    onClick={() => store.startTraining()}
                                    style={{
                                        width: '100%',
                                        color: '#fff',
                                        padding: '12px',
                                        fontSize: '14px',
                                        borderRadius: '5px',
                                        margin: 0,
                                    }}
                                />
                            )}
                            <Button
                                label="Zurück"
                                onClick={() => store.setCurrentTraining(null)}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#ff6f61',
                                    color: '#fff',
                                    padding: '12px',
                                    fontSize: '14px',
                                    borderRadius: '5px',
                                    margin: 0,
                                }}
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
});

const TrainingView = observer(({ store }: { store: any }) => {
    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}
        >
            <TrainingNavigation store={store} />
        </div>
    );
});

const TrainingTreeComponent = observer(
    ({ sdk, tree, store }: { sdk: ApplicationSdk; store: TrainingStore; tree: any }) => {
        const handleClick = (node: any) => {
            if (node.type === 'training') {
                store.setCurrentTraining(node.id);
            } else if (node.type === 'folder') {
                store.setCurrentFolder(node.id);
            }
        };

        const previousFolder = store?.previousFolder || null;

        return (
            <ul style={{ listStyleType: 'none', padding: 0 }}>
                {previousFolder ? (
                    <Button
                        label="... Zurück"
                        icon="pi pi-arrow-left"
                        size="large"
                        style={{
                            width: '100%',
                            marginBottom: '1.5rem',
                            padding: '1.25rem 1.5rem',
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            height: '60px',
                            borderRadius: '12px',
                            border: 'none',
                            color: 'white',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                        onClick={() => handleClick(previousFolder)}
                    />
                ) : null}
                {tree.length === 0 ? (
                    <li
                        style={{
                            textAlign: 'center',
                            color: '#888',
                            marginTop: '2rem',
                            fontSize: '1.2rem',
                        }}
                    >
                        Kein Inhalt in diesem Ordner
                    </li>
                ) : (
                    tree.map((node: any, index: any) => {
                        const isTraining = node.type === 'training';
                        const isFolder = node.type === 'folder';

                        return (
                            <li
                                key={index}
                                onClick={() => handleClick(node)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '15px',
                                    cursor: 'pointer',
                                    padding: '10px',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    backgroundColor: node.bgcolor,
                                }}
                            >
                                {node.img ? (
                                    <img
                                        src={node.img}
                                        alt="Item Bild"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '10%',
                                            marginRight: 10,
                                        }}
                                    />
                                ) : null}
                                <div>
                                    <h3 style={{ margin: 0, textAlign: 'left' }}>
                                        {isTraining ? (
                                            <img
                                                src={'/schulung.svg'}
                                                alt="Item Bild"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '10%',
                                                    marginTop: 5,
                                                    marginRight: 10,
                                                }}
                                            />
                                        ) : isFolder ? (
                                            <i
                                                className="pi pi-folder"
                                                style={{ marginRight: '10px' }}
                                            ></i>
                                        ) : null}

                                        {node.label}
                                        {node.completed ? (
                                            <i
                                                className="pi pi-check-circle"
                                                style={{
                                                    color: '#4CAF50',
                                                    marginLeft: 8,
                                                    fontSize: 18,
                                                }}
                                                title="Abgeschlossen"
                                            />
                                        ) : null}
                                    </h3>
                                    <p style={{ margin: 0, textAlign: 'justify' }}>
                                        {node.data || '-'}
                                    </p>
                                </div>
                            </li>
                        );
                    })
                )}
            </ul>
        );
    }
);

const TrainingComponent = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(TrainingStore);
    }, [sdk]);

    const store = sdk.currentStore as TrainingStore;
    const useTree = store?.currentTree || [];
    const useTraining = store?.currentTraining || null;

    return (
        <div
            style={{
                maxWidth: '600px',
                margin: '0 auto',
                padding: '32px',
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                minHeight: '80vh',
            }}
        >
            {useTraining ? (
                <TrainingView store={store} />
            ) : (
                <TrainingTreeComponent
                    sdk={sdk}
                    store={store}
                    tree={useTree}
                />
            )}
        </div>
    );
});

export default TrainingComponent;
